import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Monitor, 
  AppWindow,
  Layers,
  Share2,
  UserCog,
  Radar
} from 'lucide-react';
import { Link } from 'react-router-dom';
const heroSlides = [
  {
    title: "Proactive Defense. Relentless Security.",
    description: "Stop breaches before they happen with 24/7 expert-led monitoring and next-generation threat intelligence.",
    image: "/images/defensive-hero-1.png",
    linkText: "Explore Defensive Services",
    linkTo: "#services"
  },
  {
    title: "Enterprise-Grade Threat Detection",
    description: "Advanced SOC capabilities that identify and neutralize sophisticated attacks across your entire infrastructure.",
    image: "/images/defensive-hero-2.png",
    linkText: "View Security Stack",
    linkTo: "#tech-stack"
  },
  {
    title: "Resilient Security Architecture",
    description: "Building a fortress around your critical assets with Zero Trust principles and layered defense strategies.",
    image: "/images/defensive-hero-3.png",
    linkText: "Secure Your Business",
    linkTo: "/contact"
  }
];


const DefensiveSecurityPage = () => {
  useEffect(() => {
    document.title = 'Defensive Security | Atlas Defenders';

    // Add custom CSS for advanced flip card animation
    const style = document.createElement('style');
    style.textContent = `
      /* 3D Flip Card Container */
      .flip-card {
        perspective: 1000px;
        height: 180px;
        cursor: pointer;
      }
      
      .flip-card-inner {
        position: relative;
        width: 100%;
        height: 100%;
        text-align: center;
        transition: transform 0.3s ease;
        transform-style: preserve-3d;
      }
      
      .flip-card:hover .flip-card-inner {
        transform: rotateY(180deg);
      }
      
      .flip-card-front, .flip-card-back {
        position: absolute;
        width: 100%;
        height: 100%;
        backface-visibility: hidden;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .flip-card-front {
        background: white;
        border: 1px solid #e5e7eb;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }
      
      .flip-card-back {
        transform: rotateY(180deg);
        flex-direction: column;
        gap: 16px;
        padding: 20px;
        position: relative;
        overflow: hidden;
      }
      
      /* Glassmorphism effect for gradient background */
      .flip-card-back::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        backdrop-filter: blur(20px);
        background: rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        z-index: 1;
      }
      
      .flip-card-back > * {
        position: relative;
        z-index: 2;
      }
      
      /* Logo styling - Made much bigger */
      .logo-container img {
        transition: all 0.3s ease;
        max-height: 96px;
        max-width: 100%;
        object-fit: contain;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
      }
      
      /* White logo effect on flip */
      .flip-card:hover .logo-container img {
        filter: brightness(0) invert(1) !important;
      }
      
      /* For SVG logos - force all elements to white on flip */
      .flip-card:hover .logo-container svg,
      .flip-card:hover .logo-container svg *,
      .flip-card:hover .logo-container img[src$=".svg"] {
        fill: white !important;
        color: white !important;
        stroke: white !important;
      }
      
      .flip-card:hover .logo-container svg path,
      .flip-card:hover .logo-container svg text,
      .flip-card:hover .logo-container svg rect,
      .flip-card:hover .logo-container svg circle,
      .flip-card:hover .logo-container svg polygon,
      .flip-card:hover .logo-container svg g {
        fill: white !important;
        color: white !important;
        stroke: white !important;
      }
      
      /* Premium White Discover Button */
      .discover-btn {
        position: relative;
        background: rgba(255, 255, 255, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.8);
        color: #1a1a1a;
        padding: 12px 24px;
        border-radius: 12px;
        font-weight: 700;
        font-size: 13px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 
          0 4px 15px rgba(0, 0, 0, 0.15),
          0 2px 8px rgba(255, 255, 255, 0.8) inset;
        cursor: pointer;
        overflow: hidden;
      }
      
      /* Shimmer effect overlay */
      .discover-btn::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.6),
          transparent
        );
        transition: left 0.6s ease;
      }
      
      .discover-btn:hover::before {
        left: 100%;
      }
      
      .discover-btn:hover {
        background: rgba(255, 255, 255, 1);
        border-color: rgba(255, 255, 255, 1);
        transform: translateY(-2px) scale(1.05);
        box-shadow: 
          0 8px 25px rgba(0, 0, 0, 0.25),
          0 4px 12px rgba(255, 255, 255, 0.9) inset;
        color: #000;
      }
      
      .discover-btn:active {
        transform: translateY(0) scale(0.98);
        transition: all 0.1s ease;
      }
      
      /* Special handling for tools that should keep original colors */
      .flip-card:hover .keep-original-colors img {
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1)) !important;
      }
      
      .flip-card:hover .keep-original-colors svg,
      .flip-card:hover .keep-original-colors svg *,
      .flip-card:hover .keep-original-colors img[src$=".svg"] {
        fill: initial !important;
        color: initial !important;
        stroke: initial !important;
      }
      
      .flip-card:hover .keep-original-colors svg path,
      .flip-card:hover .keep-original-colors svg text,
      .flip-card:hover .keep-original-colors svg rect,
      .flip-card:hover .keep-original-colors svg circle,
      .flip-card:hover .keep-original-colors svg polygon,
      .flip-card:hover .keep-original-colors svg g {
        fill: initial !important;
        color: initial !important;
        stroke: initial !important;
      }

      /* Circuit lines animation */
      .circuit-lines {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.1;
        background-image: 
          linear-gradient(90deg, #00d4ff 1px, transparent 1px),
          linear-gradient(180deg, #00d4ff 1px, transparent 1px);
        background-size: 50px 50px;
        animation: circuitMove 20s linear infinite;
      }
      
      @keyframes circuitMove {
        0% { transform: translate(0, 0); }
        100% { transform: translate(50px, 50px); }
      }

      /* Parallax effect */
      .parallax-bg {
        transform: translateZ(0);
        will-change: transform;
      }

      /* Professional 3D Neon Hexagons */
      .professional-hexagon {
        width: 180px;
        height: 160px;
        position: relative;
        display: inline-block;
        margin: 0 25px;
        perspective: 1000px;
        cursor: pointer;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .hexagon-3d-container {
        width: 100%;
        height: 100%;
        position: relative;
        transform-style: preserve-3d;
        transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        animation: hexagonFloat 6s ease-in-out infinite;
      }
      
      .professional-hexagon:hover .hexagon-3d-container {
        transform: rotateY(15deg) rotateX(10deg) scale(1.05);
      }
      
      .neon-hexagon-border {
        width: 100%;
        height: 100%;
        background: transparent;
        position: relative;
        filter: drop-shadow(0 0 20px rgba(0, 212, 255, 0.5));
        transition: all 0.4s ease;
      }
      
      .professional-hexagon:hover .neon-hexagon-border {
        filter: drop-shadow(0 0 30px rgba(0, 212, 255, 0.8)) drop-shadow(0 0 60px rgba(0, 212, 255, 0.4));
      }
      
      .neon-hexagon-border::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #00d4ff, #0099cc, #00d4ff);
        background-size: 200% 200%;
        clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        animation: neonPulse 3s ease-in-out infinite;
      }
      
      .neon-hexagon-border::after {
        content: '';
        position: absolute;
        top: 3px;
        left: 3px;
        width: calc(100% - 6px);
        height: calc(100% - 6px);
        background: linear-gradient(135deg, #0a0a0a, #1a1a1a, #0a0a0a);
        clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        z-index: 1;
      }
      
      .hexagon-content-3d {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        z-index: 2;
        color: white;
        transition: all 0.3s ease;
      }
      
      .professional-hexagon:hover .hexagon-content-3d {
        transform: translate(-50%, -50%) translateZ(20px);
      }
      
      .neon-number {
        font-size: 3.2rem;
        font-weight: 800;
        color: #00d4ff;
        line-height: 1;
        margin-bottom: 8px;
        text-shadow: 
          0 0 10px rgba(0, 212, 255, 0.8),
          0 0 20px rgba(0, 212, 255, 0.6),
          0 0 30px rgba(0, 212, 255, 0.4);
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        letter-spacing: -1px;
        transition: all 0.3s ease;
      }
      
      .professional-hexagon:hover .neon-number {
        text-shadow: 
          0 0 15px rgba(0, 212, 255, 1),
          0 0 30px rgba(0, 212, 255, 0.8),
          0 0 45px rgba(0, 212, 255, 0.6);
        transform: scale(1.05);
      }
      
      .neon-label {
        font-size: 0.9rem;
        font-weight: 600;
        color: #ffffff;
        text-transform: uppercase;
        letter-spacing: 1px;
        opacity: 0.9;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
        transition: all 0.3s ease;
      }
      
      .professional-hexagon:hover .neon-label {
        opacity: 1;
        text-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
      }
      
      /* Floating Animation */
      @keyframes hexagonFloat {
        0%, 100% { 
          transform: translateY(0px) rotateZ(0deg); 
        }
        25% { 
          transform: translateY(-8px) rotateZ(1deg); 
        }
        50% { 
          transform: translateY(-4px) rotateZ(0deg); 
        }
        75% { 
          transform: translateY(-12px) rotateZ(-1deg); 
        }
      }
      
      /* Neon Pulse Animation */
      @keyframes neonPulse {
        0%, 100% { 
          background-position: 0% 50%;
          opacity: 1;
        }
        50% { 
          background-position: 100% 50%;
          opacity: 0.8;
        }
      }
      
      /* Professional Dark Background */
      .professional-dark-bg {
        background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%);
        position: relative;
        overflow: hidden;
      }
      
      .professional-dark-bg::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: 
          radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(0, 153, 204, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(0, 212, 255, 0.02) 0%, transparent 50%);
        background-size: 400px 400px, 600px 600px, 300px 300px;
        animation: backgroundFloat 20s ease-in-out infinite;
      }
      
      @keyframes backgroundFloat {
        0%, 100% { 
          background-position: 0% 0%, 100% 100%, 50% 50%; 
        }
        33% { 
          background-position: 30% 30%, 70% 70%, 80% 20%; 
        }
        66% { 
          background-position: 70% 10%, 30% 90%, 20% 80%; 
        }
      }
      
      /* Hexagon Shape CSS */
      .hexagon-container {
        width: 160px;
        height: 140px;
        position: relative;
        margin: 0 auto;
      }
      
      .hexagon-border {
        width: 100%;
        height: 100%;
        background: white;
        border: 4px solid #22d3ee;
        position: relative;
        clip-path: polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%);
        -webkit-clip-path: polygon(50% 0%, 93.3% 25%, 93.3% 75%, 50% 100%, 6.7% 75%, 6.7% 25%);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .hexagon-content {
        padding: 20px;
        text-align: center;
      }
      
      /* Responsive Design */
      @media (max-width: 1024px) {
        .professional-hexagon {
          width: 150px;
          height: 130px;
          margin: 0 15px;
        }
        
        .neon-number {
          font-size: 2.8rem;
        }
        
        .hexagon-shape {
          width: 140px;
          height: 120px;
        }
      }
      
      @media (max-width: 768px) {
        .professional-hexagon {
          width: 120px;
          height: 100px;
          margin: 0 10px;
        }
        
        .neon-number {
          font-size: 2.2rem;
        }
        
        .neon-label {
          font-size: 0.75rem;
        }
        
        .hexagon-shape {
          width: 120px;
          height: 100px;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);


  const [activeCategory, setActiveCategory] = useState('edr');
  const [activeWhyTab, setActiveWhyTab] = useState(0);



  // Brand color mappings based on actual logo dominant colors
  const brandColors = {
    'CrowdStrike': 'from-red-600 to-red-800',
    'Palo Alto Cortex XDR': 'from-orange-500 to-red-600',
    'Palo Alto Cortex XSOAR': 'from-orange-500 to-red-600',
    'Palo Alto': 'from-orange-500 to-red-600',
    'SentinelOne': 'from-purple-600 to-indigo-700',
    'Wazuh': 'from-blue-600 to-blue-800',
    'Cybereason': 'from-orange-600 to-red-700',
    'Symantec': 'from-yellow-500 to-orange-600',
    'Splunk SOAR': 'from-green-600 to-green-800',
    'Splunk': 'from-green-600 to-green-800',
    'LogRhythm': 'from-blue-700 to-indigo-800',
    'Graylog': 'from-gray-700 to-gray-900',
    'Security Onion': 'from-green-700 to-green-900',
    'Fortinet': 'from-red-600 to-red-800',
    'Cisco ASA / Firepower': 'from-blue-600 to-blue-800',
    'Cisco': 'from-blue-600 to-blue-800',
    'Sophos XG': 'from-blue-500 to-blue-700',
    'Sophos': 'from-blue-500 to-blue-700',
    'pfSense': 'from-blue-600 to-blue-800',
    'OPNsense': 'from-orange-600 to-orange-800',
    'Snort': 'from-pink-500 to-pink-700',
    'Suricata': 'from-orange-600 to-red-700',
    'Zeek': 'from-blue-600 to-blue-800',
    'Tenable Nessus': 'from-green-600 to-green-800',
    'Qualys': 'from-red-600 to-red-800',
    'OpenVAS': 'from-green-700 to-green-900',
    'Acunetix': 'from-orange-600 to-orange-800',
    'Rapid7': 'from-orange-600 to-red-700',
    'F5': 'from-gray-700 to-gray-900',
    'ModSecurity': 'from-blue-700 to-blue-900',
    'OWASP': 'from-blue-600 to-blue-800',
    'Keycloak': 'from-red-600 to-red-800',
    'Authentik': 'from-blue-600 to-blue-800',
    'Authelia': 'from-blue-700 to-indigo-800',
    'Okta': 'from-blue-600 to-blue-800',
    'Azure Active Directory': 'from-blue-600 to-blue-800',
    'CyberArk': 'from-red-600 to-red-800',
    'HashiCorp Vault': 'from-purple-600 to-purple-800',
    'HAProxy': 'from-blue-700 to-blue-900',
    'Squid Proxy': 'from-purple-600 to-purple-800',
    'MISP': 'from-blue-600 to-blue-800',
    'Open Threat Exchange (OTX)': 'from-blue-600 to-blue-800',
    'IBM SOAR': 'from-blue-700 to-blue-900',
    'IBM QRadar': 'from-blue-700 to-blue-900',
    'Shuffle': 'from-gray-800 to-black',
    'TheHive': 'from-orange-600 to-orange-800',
    'Elastic Stack': 'from-yellow-500 to-orange-600'
  };

  // Company website URLs for redirects
  const companyUrls = {
    'CrowdStrike': 'https://www.crowdstrike.com',
    'Palo Alto Cortex XDR': 'https://www.paloaltonetworks.com',
    'Palo Alto Cortex XSOAR': 'https://www.paloaltonetworks.com',
    'Palo Alto': 'https://www.paloaltonetworks.com',
    'SentinelOne': 'https://www.sentinelone.com',
    'Wazuh': 'https://wazuh.com',
    'Cybereason': 'https://www.cybereason.com',
    'Symantec': 'https://www.broadcom.com/products/cybersecurity',
    'Splunk SOAR': 'https://www.splunk.com',
    'Splunk': 'https://www.splunk.com',
    'LogRhythm': 'https://logrhythm.com',
    'Graylog': 'https://www.graylog.org',
    'Security Onion': 'https://securityonionsolutions.com',
    'Fortinet': 'https://www.fortinet.com',
    'Cisco ASA / Firepower': 'https://www.cisco.com',
    'Cisco': 'https://www.cisco.com',
    'Sophos XG': 'https://www.sophos.com',
    'Sophos': 'https://www.sophos.com',
    'pfSense': 'https://www.pfsense.org',
    'OPNsense': 'https://opnsense.org',
    'Snort': 'https://www.snort.org',
    'Suricata': 'https://suricata.io',
    'Zeek': 'https://zeek.org',
    'Tenable Nessus': 'https://www.tenable.com',
    'Qualys': 'https://www.qualys.com',
    'OpenVAS': 'https://www.openvas.org',
    'Acunetix': 'https://www.acunetix.com',
    'Rapid7': 'https://www.rapid7.com',
    'F5': 'https://www.f5.com',
    'ModSecurity': 'https://modsecurity.org',
    'OWASP': 'https://owasp.org',
    'Keycloak': 'https://www.keycloak.org',
    'Authentik': 'https://goauthentik.io',
    'Authelia': 'https://www.authelia.com',
    'Okta': 'https://www.okta.com',
    'Azure Active Directory': 'https://azure.microsoft.com/en-us/products/active-directory',
    'CyberArk': 'https://www.cyberark.com',
    'HashiCorp Vault': 'https://www.vaultproject.io',
    'HAProxy': 'https://www.haproxy.org',
    'Squid Proxy': 'http://www.squid-cache.org',
    'MISP': 'https://www.misp-project.org',
    'Open Threat Exchange (OTX)': 'https://otx.alienvault.com',
    'IBM SOAR': 'https://www.ibm.com/products/qradar-soar',
    'IBM QRadar': 'https://www.ibm.com/products/qradar-siem',
    'Shuffle': 'https://shuffler.io',
    'TheHive': 'https://thehive-project.org',
    'Elastic Stack': 'https://www.elastic.co',
    'Nginx': 'https://nginx.org',
    'Traefik': 'https://traefik.io'
  };

  // Technology categories with their tools
  const technologyCategories = {
    edr: {
      title: 'EDR / XDR Solutions',
      description: 'Endpoint Detection and Response / Extended Detection and Response',
      tools: [
        { name: 'CrowdStrike', logo: '/Logos/CrowdStrike_logo.svg' },
        { name: 'Palo Alto Cortex XDR', logo: '/Logos/paloalto_logo.svg' },
        { name: 'SentinelOne', logo: '/Logos/SentinelOne.svg' },
        { name: 'Wazuh', logo: '/Logos/Wazuh_Logo.svg' },
        { name: 'Cybereason', logo: '/Logos/Cyberreason.svg' },
        { name: 'Symantec', logo: '/Logos/Symantec.svg' }
      ]
    },
    soar: {
      title: 'SOAR',
      description: 'Security Orchestration, Automation, and Response',
      tools: [
        { name: 'Palo Alto Cortex XSOAR', logo: '/Logos/paloalto_logo.svg' },
        { name: 'Splunk SOAR', logo: '/Logos/splunk_logo.svg' },
        { name: 'IBM SOAR', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/IBM_logo.svg/2560px-IBM_logo.svg.png' },
        { name: 'Shuffle', logo: '/Logos/shuffle_logo.svg' },
        { name: 'TheHive', logo: '/Logos/thehive_logo.svg' }
      ]
    },
    siem: {
      title: 'SIEM',
      description: 'Security Information and Event Management',
      tools: [
        { name: 'Splunk', logo: '/Logos/splunk_logo.svg' },
        { name: 'Elastic Stack', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f4/Elasticsearch_logo.svg/2560px-Elasticsearch_logo.svg.png' },
        { name: 'LogRhythm', logo: '/Logos/logrythm_logo.png' },
        { name: 'IBM QRadar', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/IBM_logo.svg/2560px-IBM_logo.svg.png' },
        { name: 'Graylog', logo: '/Logos/graylog_logo.svg' },
        { name: 'Security Onion', logo: '/Logos/securityonion.svg' }
      ]
    },
    firewalls: {
      title: 'Firewalls (NGFW)',
      description: 'Next-Generation Firewall Solutions',
      tools: [
        { name: 'Palo Alto', logo: '/Logos/paloalto_logo.svg' },
        { name: 'Fortinet', logo: '/Logos/Fortinet_logo.svg' },
        { name: 'Cisco ASA / Firepower', logo: '/Logos/Cisco_logo.svg' },
        { name: 'Sophos XG', logo: '/Logos/Sophos_logo.svg' },
        { name: 'pfSense', logo: '/Logos/PfSense_logo.png' },
        { name: 'OPNsense', logo: '/Logos/Opnsense_logo.svg' }
      ]
    },
    ids: {
      title: 'IDS / IPS',
      description: 'Intrusion Detection & Prevention Systems',
      tools: [
        { name: 'Snort', logo: '/Logos/snort_logo.png' },
        { name: 'Suricata', logo: '/Logos/suricata_logo.png' },
        { name: 'Zeek', logo: '/Logos/Zeek_logo.png' }
      ]
    },
    vuln: {
      title: 'Vulnerability Management',
      description: 'Vulnerability Management / Scanners',
      tools: [
        { name: 'Tenable Nessus', logo: '/Logos/nessus_logo.png' },
        { name: 'Qualys', logo: '/Logos/qualys_logo.png' },
        { name: 'OpenVAS', logo: '/Logos/openvas.svg' },
        { name: 'Acunetix', logo: '/Logos/acunetix.svg' },
        { name: 'Rapid7', logo: '/Logos/rapid7.svg' }
      ]
    },
    waf: {
      title: 'Web Application Firewalls (WAF)',
      description: 'Web Application Firewall Solutions',
      tools: [
        { name: 'F5', logo: '/Logos/F5_Logo_0.svg' },
        { name: 'ModSecurity', logo: '/Logos/modesecurity_logo.svg' },
        { name: 'OWASP', logo: '/Logos/owasp_logo.svg' },
        { name: 'Palo Alto', logo: '/Logos/paloalto_logo.svg' }
      ]
    },
    iam: {
      title: 'Identity & Access Management (IAM)',
      description: 'Identity & Access Management Solutions',
      tools: [
        { name: 'Keycloak', logo: '/Logos/keycloak.svg' },
        { name: 'Authentik', logo: '/Logos/Authentik.svg' },
        { name: 'Authelia', logo: '/Logos/authelia_logo.svg' },
        { name: 'Okta', logo: '/Logos/okta_logo.svg' },
        { name: 'Azure Active Directory', logo: '/Logos/azure_active_directory_logo.svg' }
      ]
    },
    pam: {
      title: 'Privileged Access Management (PAM)',
      description: 'Privileged Access Management Solutions',
      tools: [
        { name: 'CyberArk', logo: '/Logos/Cyberark_logo.svg' },
        { name: 'HashiCorp Vault', logo: '/Logos/hashicorpvault_logo.svg' }
      ]
    },
    proxy: {
      title: 'Proxy Servers',
      description: 'Proxy Server Solutions',
      tools: [
        { name: 'HAProxy', logo: '/Logos/haproxy.svg' },
        { name: 'Squid Proxy', logo: '/Logos/squidproxy_logo.svg' },
        { name: 'F5', logo: '/Logos/F5_Logo_0.svg' },
        { name: 'Nginx', logo: '/Logos/nginx.svg' },
        { name: 'Traefik', logo: '/Logos/traefik_logo.svg' }
      ]
    },
    threat: {
      title: 'Threat Intelligence Platforms',
      description: 'Threat Intelligence Platforms and Feeds',
      tools: [
        { name: 'MISP', logo: '/Logos/misp_logo.png' },
        { name: 'Open Threat Exchange (OTX)', logo: '/Logos/otx_logo.svg' }
      ]
    }
  };

  const defensiveServices = [
    {
      icon: Monitor,
      title: 'Endpoint Protection (EDR/XDR)',
      description: 'Continuous monitoring and automated response for devices and servers.',
      features: ['Detect malware, ransomware, lateral movement', 'Isolate infected machines', 'Integrate with SIEM and SOAR']
    },
    {
      icon: AppWindow,
      title: 'Network & Perimeter Defense',
      description: 'Stop attacks at the edge before they reach your data.',
      features: ['Intrusion detection & prevention', 'Next-gen firewall deployment', 'Encrypted traffic inspection']
    },
    {
      icon: Layers,
      title: 'SIEM & Log Management',
      description: 'Centralized intelligence with real-time alerts.',
      features: ['Log collection, threat correlation', 'Anomaly detection & forensic search', 'Integrated dashboards & compliance logs']
    },
    {
      icon: Share2,
      title: 'Threat Response & Automation (SOAR)',
      description: 'Orchestrated incident response with minimal delay.',
      features: ['Automated workflows for known threats', 'Human review for high-severity alerts', 'Alert prioritization by risk']
    },
    {
      icon: UserCog,
      title: 'Vulnerability Management',
      description: 'Find and fix your weak spots — before attackers do.',
      features: ['Continuous scanning & CVSS scoring', 'Prioritized patch recommendations', 'Monthly executive reports']
    },
    {
      icon: Radar,
      title: 'Threat Intelligence & Hunting',
      description: 'Go beyond alerts — actively search for unknown attackers.',
      features: ['Custom threat feeds', 'Behavioral analysis', 'MITRE ATT&CK mapping']
    }
  ];

  const whyAtlasTabs = [
    {
      id: "support",
      label: "Support",
      title: "Support & Operations",
      bullets: [
        "24/7 Security Operations Center providing always-on threat monitoring and response.",
        "Tailored Protection with custom defense stacks for each client's unique architecture."
      ]
    },
    {
      id: "detection",
      label: "Detection",
      title: "Targeted Attack Detection",
      bullets: [
        "Real-Time Threat Detection catches incidents before damage occurs across your environment.",
        "AI + Human Expertise ensuring hybrid defense powered by automation and elite analysts."
      ]
    },
    {
      id: "response",
      label: "Response",
      title: "Rapid Incident Response",
      bullets: [
        "Fast Incident Response ensures containment in seconds, not hours.",
        "Compliance-Ready Reporting aligns with GDPR, ISO 27001, NIST, and HIPAA."
      ]
    }
  ];





  return (
    <>
      {/* Unified container for Hero and CaaS sections to eliminate any visual seam */}
      <div 
        className="relative w-full overflow-hidden"
        style={{
          background: `
            radial-gradient(ellipse 60% 90% at 100% 50%, #3b82f6 0%, #1d4ed8 22%, #1e3a8a 45%, #0f172a 70%, #070b18 100%),
            linear-gradient(to right, #060b18 0%, #0f172a 30%, #1e3a8a 60%, #1d4ed8 100%)
          `,
          backgroundBlendMode: 'screen',
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════
            HERO SECTION — TrendMicro style (split layout, blue palette)
        ═══════════════════════════════════════════════════════════════ */}
        <section
          className="relative w-full overflow-hidden"
          style={{ minHeight: '75vh' }}
        >
        {/* Single soft inner glow on the right — no visible edges */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 55% 70% at 82% 48%, rgba(96,165,250,0.22) 0%, transparent 70%)',
          }}
        />


        {/* ── SLIDE CONTENT (fills entire section via absolute) ── */}
        <div className="absolute inset-0 flex items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              className="w-full h-full flex items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              {/* LEFT: text block */}
              <div className="relative z-20 flex flex-col justify-center h-full px-8 md:px-16 lg:px-24 xl:px-32 max-w-[55%]">
                <motion.h1
                  key={`title-${currentSlide}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 24 }}
                  transition={{ duration: 0.55, ease: 'easeOut' }}
                  className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold text-white leading-[1.15] tracking-tight mb-5"
                >
                  {heroSlides[currentSlide].title}
                </motion.h1>
                <motion.p
                  key={`desc-${currentSlide}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.55, delay: 0.08, ease: 'easeOut' }}
                  className="text-slate-300 text-base md:text-lg leading-relaxed mb-8 max-w-lg font-light"
                >
                  {heroSlides[currentSlide].description}
                </motion.p>
                <motion.div
                  key={`cta-${currentSlide}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.18 }}
                >
                  <Link
                    to={heroSlides[currentSlide].linkTo}
                    className="inline-block bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold text-sm py-3 px-7 rounded transition-all duration-200 shadow-lg hover:shadow-blue-700/40"
                  >
                    {heroSlides[currentSlide].linkText}
                  </Link>
                </motion.div>
              </div>

              {/* RIGHT: Atlas Defenders brand mark centred in the blue glow */}
              <div className="relative z-20 flex-1 flex flex-col items-center justify-center h-full">
                <motion.div
                  key={`brand-${currentSlide}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  className="flex flex-col items-center select-none w-full max-w-2xl px-8 translate-y-20 md:translate-y-24"
                >
                  {/* Big Professional Logo */}
                  <img 
                    src="/Logos/atlasdefenders_white_logo.svg" 
                    alt="Atlas Defenders Logo" 
                    className="w-full h-auto max-h-[300px] md:max-h-[400px] object-contain drop-shadow-[0_0_40px_rgba(59,130,246,0.4)]"
                  />
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── LEFT ARROW ── */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 rounded-full border border-white/25 bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-all duration-200 backdrop-blur-sm hover:border-white/50"
          aria-label="Previous slide"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>

        {/* ── RIGHT ARROW ── */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 rounded-full border border-white/25 bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-all duration-200 backdrop-blur-sm hover:border-white/50"
          aria-label="Next slide"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2L10 7L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>

        {/* ── SLIDE DOTS (bottom-left, TrendMicro style) ── */}
        <div className="absolute bottom-6 left-8 md:left-16 lg:left-24 xl:left-32 z-30 flex items-center gap-3">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              aria-label={`Slide ${idx + 1}`}
              className={`transition-all duration-400 rounded-full ${
                idx === currentSlide
                  ? 'w-10 h-[3px] bg-white'
                  : 'w-5 h-[3px] bg-white/35 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </section>



      </div>



      {/* Why Atlas Defenders - Interactive Tabs Matching Image, with Blue Theme & Larger Width */}
      <section className="py-24 relative overflow-hidden bg-white">
        {/* Soft radial blue glow in the center - lighter version matching "Our Defensive Capabilities" */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-400/15 rounded-full blur-[120px] pointer-events-none z-0"></div>

        <div className="container mx-auto px-4 relative z-20 max-w-7xl xl:max-w-[1400px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-10 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 uppercase tracking-tight">
              WHY ATLAS <span className="text-blue-600">DEFENDERS ?</span>
            </h2>
            <h4 className="font-bold tracking-wide mb-8 text-xl md:text-2xl max-w-2xl mx-auto" style={{ color: '#2563eb' }}>
              Discover, consolidate, and identify critical alerts
            </h4>
          </motion.div>

          {/* Interactive Card Container */}
          <div className="w-full rounded-[24px] overflow-hidden shadow-2xl relative min-h-[480px]" style={{ background: 'linear-gradient(135deg, #060d1f 0%, #0a1a4a 35%, #0d2a8a 65%, #1d4ed8 100%)' }}>
            {/* Subtle overlay highlight matching the hero blue */}
            <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(59,130,246,0.18) 0%, transparent 70%)' }}></div>

            <div className="flex flex-col md:flex-row relative z-10 w-full min-h-[480px]">

              {/* Left Column: Navigation Tabs */}
              <div className="w-full md:w-[35%] py-12 px-8 relative flex flex-col justify-center">
                {/* Dotted line on desktop */}
                <div className="hidden md:block absolute left-[67px] top-[150px] bottom-[150px] w-px border-l border-dashed border-[#0076ce]/50 z-0"></div>

                <div className="flex flex-col gap-8 relative z-10">
                  {whyAtlasTabs.map((tab, idx) => {
                    const isActive = activeWhyTab === idx;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveWhyTab(idx)}
                        className={`relative flex items-center p-3 rounded-lg w-full transition-all duration-300 group ${isActive ? 'bg-gradient-to-r from-[rgba(0,188,212,0.2)] to-[#0076ce]' : 'hover:bg-white/5'}`}
                      >
                        {/* Icon Circle */}
                        <div className={`w-12 h-12 rounded-full flex flex-col items-center justify-center shrink-0 border z-10 transition-colors duration-300 ${isActive ? 'bg-white border-white text-[#001f5f]' : 'bg-[#000d26] border-[#0076ce]/50 text-[#00BCD4] group-hover:border-[#00BCD4] group-hover:text-white'}`}>
                          <div className={`w-3.5 h-[2px] mb-[3px] rounded-full ${isActive ? 'bg-[#0076ce]' : 'bg-current'}`}></div>
                          <div className={`w-3.5 h-[2px] rounded-full ${isActive ? 'bg-[#0076ce]' : 'bg-current'}`}></div>
                        </div>

                        {/* Tab Label */}
                        <span className={`ml-6 text-xl font-bold transition-colors duration-300 ${isActive ? 'text-white' : 'text-blue-200 group-hover:text-white'}`}>
                          {tab.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right Column: Content */}
              <div className="w-full md:w-[65%] p-10 md:p-14 flex flex-col justify-center border-t md:border-t-0 md:border-l border-[#0076ce]/30">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeWhyTab}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.25 }}
                  >
                    <h3 className="text-white text-[28px] font-bold mb-8 tracking-tight">
                      {whyAtlasTabs[activeWhyTab].title}
                    </h3>

                    <ul className="space-y-6">
                      {whyAtlasTabs[activeWhyTab].bullets.map((bullet, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="flex-shrink-0 mt-[3px] mr-4 w-[18px] h-[18px] bg-[#00BCD4] rounded-full flex items-center justify-center shadow-[0_0_12px_rgba(0,188,212,0.5)]">
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-blue-50 text-[16px] leading-relaxed font-normal">
                            {bullet}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </AnimatePresence>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* Services Grid - Image Style matched layout */}
      <section className="py-24 bg-[#f8faff] relative overflow-hidden">
        {/* Soft radial blue glow in the center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-400/20 rounded-full blur-[120px] pointer-events-none z-0"></div>

        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 uppercase tracking-tight">
              Our Defensive <span className="text-blue-600">Capabilities</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto font-light leading-relaxed">
              Comprehensive protection across your entire digital infrastructure.
            </p>
          </motion.div>

          {/* Grid matching image exact dimensions */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {defensiveServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 border border-slate-50/50 flex flex-col items-center text-center h-full"
              >
                {/* Large Thin Blue Line-Art Icon */}
                <div className="mb-6 text-blue-600">
                  <service.icon size={52} strokeWidth={1} />
                </div>

                <h3 className="text-[22px] font-normal text-slate-800 mb-5 leading-[1.3]">
                  {service.title}
                </h3>

                <p className="text-[15px] text-slate-500 leading-[1.6] font-light">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>




      {/* Technology Stack Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 uppercase tracking-tight">
              Our Technology <span className="text-blue-600">Stack</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We leverage industry-leading security tools and platforms to deliver comprehensive protection.
            </p>
          </motion.div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {Object.entries(technologyCategories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${activeCategory === key
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {category.title}
              </button>
            ))}
          </div>

          {/* Active Category Display */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="text-center mb-12"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                {technologyCategories[activeCategory as keyof typeof technologyCategories].title}
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {technologyCategories[activeCategory as keyof typeof technologyCategories].description}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Tools Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
            >
              {technologyCategories[activeCategory as keyof typeof technologyCategories].tools.map((tool, index) => {
                // Special styling logic for specific tools
                const isTheHive = tool.name === 'TheHive';
                const isOpenVAS = tool.name === 'OpenVAS';
                const isShuffle = tool.name === 'Shuffle';
                const isHAProxy = tool.name === 'HAProxy';
                const isSquidProxy = tool.name === 'Squid Proxy';
                const isTraefik = tool.name === 'Traefik';
                const isCybereason = tool.name === 'Cybereason';
                const isAcunetix = tool.name === 'Acunetix';
                const isModSecurity = tool.name === 'ModSecurity';
                const isAuthentik = tool.name === 'Authentik';

                // Special logo sizing
                const getLogoClasses = () => {
                  if (isTheHive) return "w-full h-full object-contain scale-150"; // Very big logo for TheHive
                  if (isHAProxy || isSquidProxy || isTraefik) return "w-full h-full object-contain scale-300"; // VERY VERY VERY big logos for proxy servers
                  if (isCybereason || isOpenVAS || isAcunetix || isModSecurity || isAuthentik) return "w-full h-full object-contain scale-200"; // Bigger logos for requested tools
                  return "w-full h-full object-contain";
                };

                // Special front card styling for Shuffle and specific tools
                const getFrontCardClasses = () => {
                  if (isShuffle) return "flip-card-front bg-gray-900 border-gray-700";
                  if (isModSecurity) return "flip-card-front bg-blue-50 border-blue-200";
                  if (isAuthentik) return "flip-card-front bg-blue-50 border-blue-200";
                  if (isAcunetix) return "flip-card-front bg-orange-50 border-orange-200";
                  return "flip-card-front";
                };

                // Special logo container classes for specific tools
                const getLogoContainerClasses = () => {
                  if (isShuffle) return "logo-container p-4 flex flex-col items-center justify-center h-full keep-original-colors";
                  if (isModSecurity || isAuthentik || isAcunetix) return "logo-container p-4 flex flex-col items-center justify-center h-full keep-original-colors";
                  return "logo-container p-4 flex flex-col items-center justify-center h-full";
                };

                return (
                  <motion.div
                    key={tool.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="flip-card"
                    onClick={() => {
                      const url = companyUrls[tool.name as keyof typeof companyUrls];
                      if (url) window.open(url, '_blank');
                    }}
                  >
                    <div className="flip-card-inner">
                      <div className={getFrontCardClasses()}>
                        <div className={getLogoContainerClasses()}>
                          <div className={`${isTheHive ? 'flex-1' : 'w-full h-full'} flex items-center justify-center`}>
                            <img
                              src={tool.logo}
                              alt={tool.name}
                              className={getLogoClasses()}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `<div class="text-gray-600 font-semibold text-sm text-center">${tool.name}</div>`;
                                }
                              }}
                            />
                          </div>
                          {/* Show name below logo for specific tools */}
                          {(isTheHive || tool.name === 'HashiCorp Vault') && (
                            <div className={`font-semibold text-sm text-center mt-2 ${isShuffle ? 'text-white' : 'text-gray-700'}`}>
                              {tool.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className={`flip-card-back bg-gradient-to-br ${brandColors[tool.name as keyof typeof brandColors] || 'from-gray-600 to-gray-800'}`}
                      >
                        <div className="text-white font-bold text-lg text-center mb-2">
                          {tool.name}
                        </div>
                        <button className="discover-btn">
                          Discover
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Why Choose Atlas Defenders - CrowdStrike Style (Blue/White Refined) */}
      <section className="bg-[#001F5F] relative overflow-hidden flex items-center py-24 lg:py-0 lg:h-[800px]">
        {/* Background Gradients */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-blue-900/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-blue-900/10 blur-3xl rounded-full"></div>
        </div>

        <div className="w-full relative z-10 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-full">

            {/* Left Content - TEXT & STATS */}
            <div className="flex flex-col justify-center px-6 lg:pl-24 lg:pr-16 z-20 order-1 py-16 lg:py-0">
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
                  Today's adversaries are smarter and stealthier than ever.
                </h2>
                <p className="text-blue-100/80 text-lg md:text-xl font-light max-w-xl">
                  Legacy tools aren't built for cross-domain attacks and modern ransomware. Atlas Defenders delivers the speed and intelligence you need.
                </p>
              </div>

              {/* 4-Grid Stats/Points Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12">
                {/* Item 1 */}
                <div className="flex items-start">
                  <span className="text-5xl font-bold text-white mr-4 leading-none">1.</span>
                  <div className="text-white text-lg leading-snug">
                    <span className="font-bold text-[#00BCD4]">Elite Certified Analysts</span>
                    <br />
                    <span className="text-blue-100/70 text-base">CEH, OSCP, and CRTO experts hunting 24/7.</span>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="flex items-start">
                  <span className="text-5xl font-bold text-white mr-4 leading-none">2.</span>
                  <div className="text-white text-lg leading-snug">
                    <span className="font-bold text-[#00BCD4]">Lightning-Fast Response</span>
                    <br />
                    <span className="text-blue-100/70 text-base">Minutes to containment, not days.</span>
                  </div>
                </div>

                {/* Item 3 */}
                <div className="flex items-start">
                  <span className="text-5xl font-bold text-white mr-4 leading-none">3.</span>
                  <div className="text-white text-lg leading-snug">
                    <span className="font-bold text-[#00BCD4]">Enterprise-Grade Defense</span>
                    <br />
                    <span className="text-blue-100/70 text-base">Fortune 500 security tailored for any size.</span>
                  </div>
                </div>

                {/* Item 4 */}
                <div className="flex items-start">
                  <span className="text-5xl font-bold text-white mr-4 leading-none">4.</span>
                  <div className="text-white text-lg leading-snug">
                    <span className="font-bold text-[#00BCD4]">Transparent Reporting</span>
                    <br />
                    <span className="text-blue-100/70 text-base">Full visibility into every action taken.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - IMAGE */}
            <div className="relative h-full hidden lg:flex items-center justify-center order-2">
              <div className="relative w-full h-full">
                <img
                  src="/images/Cyberdefense2.png"
                  alt="Advanced Threat Protection"
                  className="w-full h-full object-cover object-center absolute inset-0"
                />

                {/* Gradient overlays to blend image */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#001F5F] via-[#001F5F]/40 to-transparent z-10 w-1/2"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#001F5F] via-transparent to-transparent z-10 h-1/3 mt-auto"></div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Security Excellence Certified Section */}
      <section className="py-20 bg-white">
        <div className="container max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Security Excellence Certified
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Showcasing our commitment to excellence with industry-recognized certifications that ensure top-tier security solutions
            </p>
          </motion.div>

          {/* Professional Certification Badges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex justify-center items-center space-x-12"
          >
            {/* CISSP */}
            <img src="/Certifications/CISSP.png" alt="CISSP" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

            {/* CISM */}
            <img src="/Certifications/Certified Information Security Manager.png" alt="CISM" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

            {/* CISA */}
            <img src="/Certifications/Certified Information Systems Auditor.png" alt="CISA" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

            {/* OSDA */}
            <img src="/Certifications/OSDA.svg" alt="OSDA" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

            {/* CySA+ */}
            <img src="/Certifications/Comptia Cysa+.png" alt="CySA+" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

            {/* CDSA */}
            <img src="/Certifications/cdsa.svg" alt="CDSA" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

            {/* Security+ */}
            <img src="/Logos/security+_logo.png" alt="Security+" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Kaspersky Banner Style (Refined: Deep Blue & White) */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-[#001F5F] rounded-sm p-10 md:p-14 flex flex-col lg:flex-row items-center justify-between gap-12 shadow-2xl relative overflow-hidden"
          >
            {/* Left Side - Text */}
            <div className="flex-1 max-w-3xl relative z-10 text-center lg:text-left">
              <div className="text-xs md:text-sm font-bold tracking-widest uppercase mb-4 text-blue-300">
                TAKE ACTION
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Ready to Strengthen Your Defenses?
              </h2>
              <p className="text-lg md:text-xl text-blue-100 font-medium leading-relaxed max-w-2xl">
                Don't wait for a breach to happen. Get proactive protection with our comprehensive defensive security services.
              </p>
            </div>

            {/* Right Side - Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 relative z-10 flex-shrink-0 w-full lg:w-auto">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-white hover:bg-gray-100 text-[#001F5F] px-8 py-4 rounded-sm font-bold text-base transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
              >
                Get Your Free Assessment
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-[#001F5F] px-8 py-4 rounded-sm font-bold text-base transition-all duration-300 w-full sm:w-auto"
              >
                View All Services
              </Link>
            </div>

            {/* Subtle Texture Overlay */}
            <div className="absolute top-0 right-0 p-16 opacity-10 pointer-events-none">
              <Shield size={400} className="text-white transform rotate-12 translate-x-1/2 -translate-y-1/2" />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default DefensiveSecurityPage;